// Tanstack Start server fn — evaluates every feature flag for the current
// authenticated Club in one parallel pass and returns the
// `Record<FlagKey, boolean>` map the nav (`useFeatureFlag`) and the
// `/live-stream` route guard read from the Tanstack Query cache.
//
// **File naming:** `.fn.ts` (not `.server.ts`) because client code imports the
// callable — Tanstack Start's RPC bridge runs the handler on the server. It is
// coverage-excluded by the `*.fn.ts` glob; the parallel-eval logic, query key,
// cache-warming policy, and no-session guard it delegates to live in
// `evaluate-flags.ts`, which the unit tests cover directly. The server-only
// imports stay inside the handler so the client bundle never pulls in the
// Flagship/composition modules.
//
// A request with no resolvable session THROWS (`UnauthenticatedFlagsError`)
// rather than returning a dark map: the result lands in a cacheable position
// (the per-Club flags query), and a transient no-session response cached under
// a real Club's key would pin the nav dark for the staleTime window. Callers
// fail safe on rejection — nothing is cached, `useFeatureFlag` reads per-key
// defaults, and the `_authenticated` guard redirects the expired session to
// /login anyway.

import { createServerFn } from '@tanstack/react-start';
import { getRequest } from '@tanstack/react-start/server';

import type { FlagKey } from './flags';

export const evaluateFeatureFlagsServerFn = createServerFn({
	method: 'GET',
}).handler(async (): Promise<Record<FlagKey, boolean>> => {
	const { getServerAuthService } = await import(
		'~/bounded-contexts/authentication/composition.server'
	);
	const { getServerFeatureFlags } = await import('./composition.server');
	const { assertFlagsScope, evaluateAllFlags } = await import(
		'./evaluate-flags'
	);

	const scope = assertFlagsScope(
		await getServerAuthService().resolveTenantScope(getRequest()),
	);

	const featureFlags = await getServerFeatureFlags();
	return evaluateAllFlags(featureFlags, scope.clubId);
});
